import { motion } from "framer-motion";
import Button from "@components/Button";
import { useEffect, useState } from "react";
import CountUp from "@components/CountUp";
import Carousel from "@components/Carousel";
import debounce from "lodash/debounce";

export default function HeroSectionUno() {
	const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
	const [imageLoaded, setImageLoaded] = useState(false);
	const [showContent, setShowContent] = useState(false);

	useEffect(() => {
		const handleResize = debounce(() => {
			setIsMobile(window.innerWidth < 768);
		}, 100);
		window.addEventListener("resize", handleResize);
		return () => {
			window.removeEventListener("resize", handleResize);
			handleResize.cancel();
		};
	}, []);

	const cardData = [
		{ target: 6, text: "Years Experience" },
		{ target: 200, text: "Passed Tests" },
		{ target: 200, text: "Students" },
		{ target: 100, text: "Reviews" },
	];

	useEffect(() => {
		const timer = setTimeout(() => {
			setShowContent(true);
		}, 100);
		return () => clearTimeout(timer);
	}, []);

	const gridContainerVariants = {
		hidden: { opacity: 0 },
		visible: {
			opacity: 1,
			transition: {
				staggerChildren: 0.2,
				delayChildren: 0.1,
			},
		},
	};

	const slideInUp = {
		hidden: { opacity: 0, y: 50 },
		visible: {
			opacity: 1,
			y: 0,
			transition: {
				duration: 0.6,
				ease: "easeOut",
			},
		},
	};

	const slideInLeft = {
		hidden: { opacity: 0, x: -50 },
		visible: {
			opacity: 1,
			x: 0,
			transition: {
				duration: 0.8,
				ease: "easeOut",
			},
		},
	};

	const slideInRight = {
		hidden: { opacity: 0, x: 50 },
		visible: {
			opacity: 1,
			x: 0,
			transition: {
				duration: 0.8,
				ease: "easeOut",
			},
		},
	};

	const contentVariants = {
		hidden: { opacity: 0, y: 20 },
		visible: {
			opacity: 1,
			y: 0,
			transition: {
				duration: 0.5,
				ease: "easeOut",
			},
		},
	};

	const Skeleton = ({ className }) => (
		<div className={`animate-pulse bg-gray-300 rounded ${className}`} />
	);

	return (
		<div className="w-full h-full bg-steerBg flex items-center justify-center">
			<motion.div
				className="w-full grid grid-cols-1 lg:grid-cols-12 gap-4 h-full"
				initial="hidden"
				animate={showContent ? "visible" : "hidden"}
				variants={gridContainerVariants}
			>
				<motion.div
					className="bg-white/60 flex md:hidden backdrop-blur-md aspect-video rounded-2xl relative overflow-hidden"
					variants={slideInLeft}
				>
					<img
						src="/images/home-hero2.webp"
						alt="Hero Banner"
						className="w-full h-full object-cover object-center"
						loading="eager"
						fetchpriority="high"
						width="1600"
						height="900"
					/>
				</motion.div>
				{/* LEFT COLUMN */}
				<motion.div
					className="lg:col-span-4 overflow-hidden flex flex-col gap-4 h-full min-h-0"
					variants={slideInRight}
				>
					<div className="flex flex-col gap-4 p-8 md:p-12 bg-white/60 rounded-2xl backdrop-blur-md flex-1 justify-center min-h-0">
						<motion.div
							variants={contentVariants}
							className="text-steerOrange text-xs font-semibold uppercase tracking-wider"
						>
							ACT-Accredited Driving School
						</motion.div>

						<motion.h1
							className="text-2xl md:text-3xl font-extrabold text-steerBlack leading-tight"
							variants={contentVariants}
						>
							Steer Smart driving school Canberra ACT
						</motion.h1>

						<motion.p
							className="text-steerLightBlack text-sm md:text-base leading-relaxed"
							variants={contentVariants}
						>
							Steer Smart driving school Canberra ACT comprises expert driving instructors who provide tailored automatic and manual driving lessons.
<br /><br />
This is a great option for beginners, those looking to prepare for the driving test, and also for refresher drivers who want to pass and drive confidently.
						</motion.p>

						<motion.div
							variants={contentVariants}
							className="flex flex-wrap items-center gap-4 mt-4"
						>
							<Button
								buttonName="Book Your First Lesson"
								buttonStyle="secondary"
								buttonLink="/#packages-section"
							/>
							{/* <a
								href="tel:+61450487804"
								className="text-steerBlack font-semibold underline-offset-4 hover:underline"
							>
								Call Now
							</a> */}
						</motion.div>
					</div>

					{/* Carousel */}
					<motion.div
						className="col-span-1 md:col-span-2 flex items-center justify-center overflow-hidden relative rounded-2xl border border-steerBlack/10 backdrop-blur"
						variants={slideInUp}
					>
						<Carousel
							baseWidth={isMobile ? 220 : 400}
							autoplay={true}
							autoplayDelay={3000}
							pauseOnHover={true}
							loop={true}
						/>
					</motion.div>
				</motion.div>
				{/* RIGHT COLUMN — IMAGE */}
				<motion.div
					className="lg:col-span-8 flex flex-col justify-between gap-4"
					variants={gridContainerVariants}
				>
					{/* Main Text Card */}
					<motion.div
						className="bg-white/60 hidden md:flex backdrop-blur-md aspect-video rounded-2xl relative overflow-hidden"
						variants={slideInLeft}
					>
						<img
							src="/images/home-hero2.jpg"
							alt="Hero Banner"
							className="w-full h-full object-cover object-center"
							loading="eager"
							fetchpriority="high"
						/>
					</motion.div>

					{/* Stats + Carousel Row */}
					<motion.div
						className="grid grid-cols-1 md:grid-cols-4 gap-4"
						variants={gridContainerVariants}
					>
						{/* Left Stats */}
						<div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 h-full col-span-1 md:col-span-4">
							{cardData.map((card, index) => (
								<motion.div
									key={`stat-${index}`}
									className="bg-white/60 backdrop-blur border border-steerBlack/10 rounded-2xl flex flex-col justify-center items-center p-4"
									variants={slideInUp}
								>
									<span className="text-xl md:text-3xl font-bold text-steerOrange">
										<CountUp
											from={0}
											to={card.target}
											separator=","
											duration={2}
										/>
										+
									</span>
									<span className="text-[10px] md:text-xs text-steerLightBlack font-semibold uppercase mt-1 text-center">
										{card.text}
									</span>
								</motion.div>
							))}
						</div>

						{/* Right Stats */}
						{/* <div className="flex flex-col gap-3 md:gap-4 h-full col-span-1">
							{cardData.slice(2, 4).map((card, index) => (
								<motion.div
									key={`right-${index}`}
									className="bg-white/60 backdrop-blur border border-steerBlack/10 rounded-2xl flex flex-col justify-center items-center p-2 flex-grow"
									variants={slideInUp}
								>
									<span className="text-xl md:text-3xl font-bold text-steerBlack">
										<CountUp
											from={0}
											to={card.target}
											separator=","
											duration={2}
										/>
										+
									</span>
									<span className="text-[10px] md:text-xs text-steerLightBlack font-semibold uppercase mt-1 text-center">
										{card.text}
									</span>
								</motion.div>
							))}
						</div> */}
					</motion.div>
				</motion.div>
				{/* RIGHT COLUMN — IMAGE */}
			</motion.div>
		</div>
	);
}
